import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import type {
  SupportedCurrency,
  TokenPaymentMethod,
} from '@sorare/core/src/__generated__/globalTypes';
import { Currency } from '@sorare/core/src/__generated__/globalTypes';
import { HeadlineS } from '@sorare/core/src/atoms/typography';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { useSnackNotificationContext } from '@sorare/core/src/contexts/snackNotification';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { sendSafeError } from '@sorare/core/src/lib/error';
import { payment } from '@sorare/core/src/lib/glossary';
import { formatGqlErrors } from '@sorare/core/src/lib/gql';
import type { MonetaryAmount } from '@sorare/core/src/lib/monetaryAmount';

import { LazyPaymentProvider } from '#src/components/buyActions/LazyPaymentProvider/index.tsx';
import { CardOverview } from '#src/components/buyActions/PaymentBox/CardOverview/index.tsx';
import { PromoBanners } from '#src/components/market/PromoBanners/index.tsx';
import type { PrimaryBuyConfirmationOptions } from '#src/contexts/buyingConfirmation/index.tsx';
import { useBuyConfirmationContext } from '#src/contexts/buyingConfirmation/index.tsx';
import { useAcceptOffer } from '#src/hooks/offers/useAcceptOffer.ts';
import { usePollPrimaryOfferBuyer } from '#src/hooks/usePollPrimaryOfferBuyer.tsx';

import type {
  PrimaryBuyPaymentFlowQuery,
  PrimaryBuyPaymentFlowQueryVariables,
} from './__generated__/index.graphql.ts';
import { useRefreshSignedAmount } from './useRefreshSignedAmount.ts';

type Props = {
  offerId: string;
  primaryBuyConfirmationOptions?: PrimaryBuyConfirmationOptions;
  onSuccess?: () => void;
  onClose: () => void;
  onBuyConfirmationClose?: () => void;
  showBuyConfirmation?: boolean;
  onlyCreditCards?: boolean;
  applyConversionCredits?: boolean;
  beforeSubmitCallback?: () => Promise<void>;
};

const PRIMARY_BUY_PAYMENT_FLOW_QUERY = gql`
  query PrimaryBuyPaymentFlowQuery($id: String!) {
    tokens {
      primaryOffer(id: $id) {
        id
        cardPaymentProvider
        price {
          ...useMonetaryAmount_monetaryAmount
        }
        signedAmount
        anyCards {
          slug
          sport
          ...CardOverview_anyCard
          ...useAcceptOffer_anyCard
        }
        ...usePollPrimaryOfferBuyer_primaryOffer
        ...PromoBanners_PromotionalEventsInterface
        ...PromoBanners_WheelTicketRewardsInterface
      }
    }
  }
  ${useAcceptOffer.fragments.anyCard}
  ${CardOverview.fragments.anyCard}
  ${usePollPrimaryOfferBuyer.fragments.primaryOffer}
  ${PromoBanners.fragments.PromotionalEventsInterface}
  ${PromoBanners.fragments.WheelTicketRewardsInterface}
  ${useMonetaryAmount.fragments.monetaryAmount}
` as TypedDocumentNode<
  PrimaryBuyPaymentFlowQuery,
  PrimaryBuyPaymentFlowQueryVariables
>;

export const PrimaryBuyFlow = ({
  offerId,
  onClose,
  primaryBuyConfirmationOptions,
  onSuccess,
  onBuyConfirmationClose,
  showBuyConfirmation = true,
  onlyCreditCards = false,
  applyConversionCredits,
}: Props) => {
  const { formatMessage } = useIntlContext();
  const acceptOffer = useAcceptOffer();
  const { setShowBuyingConfirmation } = useBuyConfirmationContext();
  const refresh = useRefreshSignedAmount(idFromObject(offerId));
  const { showNotification } = useSnackNotificationContext();
  const { toMonetaryAmount } = useMonetaryAmount();

  const { data, error } = useQuery(PRIMARY_BUY_PAYMENT_FLOW_QUERY, {
    variables: {
      id: idFromObject(offerId),
    },
  });

  const primaryOffer = data?.tokens?.primaryOffer;

  const onPaymentSuccess = useCallback(() => {
    if (showBuyConfirmation) {
      setShowBuyingConfirmation(true);
    }
    onSuccess?.();
    onClose();
  }, [showBuyConfirmation, setShowBuyingConfirmation, onSuccess, onClose]);

  const [polling, startPolling] = usePollPrimaryOfferBuyer(
    primaryOffer,
    onPaymentSuccess
  );

  const [priceInfos, setPriceInfos] = useState<
    | {
        price: MonetaryAmount | null;
        signedAmount: string | null;
      }
    | undefined
  >(undefined);

  if (!priceInfos && primaryOffer) {
    setPriceInfos({
      price: toMonetaryAmount(primaryOffer.price),
      signedAmount: primaryOffer.signedAmount,
    });
  }

  if (!primaryOffer) return null;

  if (error) {
    // This should not happen. Temporary added for monitoring purposes.
    sendSafeError(`Offer not found for id: ${offerId}`);
    showNotification('errors', { errors: [error.message] });
    onClose();
    return null;
  }

  const { price, anyCards, signedAmount } = primaryOffer;

  const refreshPriceAndSignedAMountBeforeBuy = async () => {
    const refreshResult = await refresh();
    if (refreshResult?.error)
      return { err: formatGqlErrors([refreshResult.error]) };
    const { price: refreshPrice, signedAmount: refreshSignedAmount } =
      refreshResult?.data?.tokens?.primaryOffer || {};
    if (
      price &&
      refreshSignedAmount &&
      refreshPrice &&
      refreshSignedAmount !== priceInfos?.signedAmount
    ) {
      setPriceInfos({
        price: toMonetaryAmount(refreshPrice),
        signedAmount: refreshSignedAmount,
      });
      if (!toMonetaryAmount(refreshPrice).isEqualTo(toMonetaryAmount(price))) {
        // if price has changed, we need to update the payment box
        return {
          warnings: [
            formatMessage({
              defaultMessage:
                'The price has changed, please review the offer and try again.',
            }),
          ],
        };
      }
    }
    return { signedAmount: refreshSignedAmount };
  };

  const buy = async ({
    useAvailableCredits,
    supportedCurrency,
    tokenPaymentMethod,
    applePayPaymentToken,
  }: {
    useAvailableCredits: boolean;
    supportedCurrency: SupportedCurrency;
    tokenPaymentMethod?: TokenPaymentMethod;
    applePayPaymentToken?: ApplePayJS.ApplePayPaymentToken;
  }) => {
    const refreshResult = await refreshPriceAndSignedAMountBeforeBuy();
    if (refreshResult?.err?.length) return refreshResult;
    if (!refreshResult.signedAmount || !signedAmount)
      return {
        err: [
          formatMessage({
            defaultMessage: 'The card is unavailable, please try again later.',
          }),
        ],
      };

    const { errors, deal } = await acceptOffer({
      offerId,
      receiveCards: anyCards,
      useAvailableCredits,
      supportedCurrency,
      attemptReference: null,
      tokenPaymentMethod,
      signedAmount: refreshResult.signedAmount || signedAmount || undefined,
      applePayPaymentToken,
    });

    if (deal) {
      return onPaymentSuccess();
    }
    return { err: errors };
  };

  if (!price || !signedAmount) return null;

  return (
    <LazyPaymentProvider
      paymentProps={{
        canChangeRefCurrency: true,
        objectId: offerId,
        cardPaymentProvider: primaryOffer.cardPaymentProvider,
        onSuccess: () => startPolling(),
        onBeforeBuyWithCreditCard: refreshPriceAndSignedAMountBeforeBuy,
        signedAmount: signedAmount || undefined,
        onSubmit: buy,
        price,
        cta: payment.confirmAndPay,
        applyPaymentFees: true,
        canUseConversionCredit: true,
        currencies: [Currency.FIAT, Currency.ETH],
        sport: anyCards[0].sport,
        onlyCreditCards,
        useKlarna: true,
        confirmationProviderStateProps: {
          primaryBuyId: offerId,
          primaryBuyConfirmationOptions,
          onClose: onBuyConfirmationClose,
        },
        applyConversionCredits,
      }}
      paymentBoxProps={{
        loadingPolling: polling,
        banner: (
          <PromoBanners
            promotionalEvents={primaryOffer}
            wheelTicketRewards={primaryOffer}
          />
        ),
      }}
      paymentDialogProps={{
        onClose,
        title: (
          <HeadlineS as="h5">
            <FormattedMessage {...payment.paymentBoxTitle} />
          </HeadlineS>
        ),
        tokenPreview: <CardOverview cards={anyCards} soldOnPrimary />,
      }}
    />
  );
};
